import { useRef, useState } from 'react'
import {
  getOpticalMeasureDistanceRange,
  type FaceData,
  type OpticalMeasureFramingDistanceProfileSettings,
} from '../index'
import {
  BUFFER_SIZE,
  EYE_OPEN_THRESHOLD,
  ROTATION_X_THRESHOLD,
  SAMPLE_DURATION_MS,
  STABLE_FOR_MS,
  VARIANCE_THRESHOLD,
  ROTATION_Y_THRESHOLD,
} from './constants'
import { Phase } from './types'

type MeasurementOptions<Sample, Result, StartArg> = {
  distanceProfile: OpticalMeasureFramingDistanceProfileSettings
  extractSample: (incoming: FaceData) => Sample | null
  variance: (samples: Sample[]) => number
  average: (samples: Sample[]) => Result
  onStart?: (arg?: StartArg) => void
  onStop?: () => void
  handleCalibrating?: (incoming: FaceData) => boolean
  handleMissingSample?: (incoming: FaceData, now: number) => void
  resetMissingSample?: () => void
  ignoreMissingSample?: (incoming: FaceData) => boolean
  bufferSize?: number
  varianceThreshold?: number
  stableForMs?: number
  sampleDurationMs?: number
}

export function useStableMeasurement<Sample, Result, StartArg = void>({
  distanceProfile,
  extractSample,
  variance,
  average,
  onStart,
  onStop,
  handleCalibrating,
  handleMissingSample,
  resetMissingSample,
  ignoreMissingSample,
  bufferSize = BUFFER_SIZE,
  varianceThreshold = VARIANCE_THRESHOLD,
  stableForMs = STABLE_FOR_MS,
  sampleDurationMs = SAMPLE_DURATION_MS,
}: MeasurementOptions<Sample, Result, StartArg>) {
  const distanceRange = getOpticalMeasureDistanceRange(
    distanceProfile,
    'guidance'
  )
  const [phase, setPhase] = useState<Phase>('idle')
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState<Result | null>(null)

  const activeRef = useRef(false)
  const samplesRef = useRef<Sample[]>([])
  const bufferRef = useRef<Sample[]>([])
  const stableStartRef = useRef<number | null>(null)
  const sampleStartRef = useRef<number | null>(null)
  const lastProgressRef = useRef(0)

  const resetStability = () => {
    stableStartRef.current = null
    bufferRef.current = []
  }

  const start = (arg?: StartArg) => {
    activeRef.current = true
    samplesRef.current = []
    lastProgressRef.current = 0
    sampleStartRef.current = null
    resetStability()
    resetMissingSample?.()
    onStart?.(arg)
    setProgress(0)
    setResult(null)
    setPhase('detecting')
  }

  const stop = () => {
    activeRef.current = false
    sampleStartRef.current = null
    resetStability()
    resetMissingSample?.()
    onStop?.()
    setPhase('idle')
    setProgress(0)
  }

  const clear = () => {
    setResult(null)
    setPhase('idle')
  }

  const handleFaceData = (incoming: FaceData) => {
    if (!activeRef.current) return

    const now = Date.now()

    if (handleCalibrating?.(incoming)) {
      resetStability()
      sampleStartRef.current = null
      setPhase('calibrating')
      return
    }

    if (!incoming.detected) {
      handleMissingSample?.(incoming, now)
      resetStability()
      sampleStartRef.current = null
      setPhase('detecting')
      return
    }

    resetMissingSample?.()

    if (incoming.distanceCm < distanceRange.min) {
      resetStability()
      sampleStartRef.current = null
      setPhase('tooClose')
      return
    }

    if (incoming.distanceCm > distanceRange.max) {
      resetStability()
      sampleStartRef.current = null
      setPhase('tooFar')
      return
    }

    const centered =
      Math.abs(incoming.rotationX) < ROTATION_X_THRESHOLD &&
      Math.abs(incoming.rotationY) < ROTATION_Y_THRESHOLD

    if (!centered) {
      resetStability()
      sampleStartRef.current = null
      setPhase('positioning')
      return
    }

    const eyesOpen =
      (incoming.eyeOpenness?.left ?? 0) >= EYE_OPEN_THRESHOLD &&
      (incoming.eyeOpenness?.right ?? 0) >= EYE_OPEN_THRESHOLD

    if (!eyesOpen) {
      resetStability()
      sampleStartRef.current = null
      setPhase('opening')
      return
    }

    const sample = extractSample(incoming)
    if (sample === null) {
      if (ignoreMissingSample?.(incoming)) return
      resetStability()
      sampleStartRef.current = null
      return
    }

    bufferRef.current = [...bufferRef.current.slice(-(bufferSize - 1)), sample]

    const stable =
      bufferRef.current.length >= bufferSize &&
      variance(bufferRef.current) < varianceThreshold

    if (sampleStartRef.current !== null) {
      samplesRef.current.push(sample)

      if (!stable) {
        sampleStartRef.current = null
        samplesRef.current = []
        resetStability()
        setProgress(0)
        setPhase('stabilising')
        return
      }

      const elapsed = now - sampleStartRef.current
      const nextProgress = Math.min(elapsed / sampleDurationMs, 1)

      if (nextProgress - lastProgressRef.current > 0.02 || nextProgress >= 1) {
        lastProgressRef.current = nextProgress
        setProgress(nextProgress)
      }

      if (elapsed >= sampleDurationMs) {
        activeRef.current = false
        sampleStartRef.current = null
        setResult(average(samplesRef.current))
        setPhase('complete')
      }
      return
    }

    if (!stable) {
      stableStartRef.current = null
      setPhase('stabilising')
      return
    }

    if (!stableStartRef.current) stableStartRef.current = now

    const stableFor = now - stableStartRef.current
    if (stableFor >= stableForMs) {
      sampleStartRef.current = now
      samplesRef.current = []
      setPhase('sampling')
      return
    }

    setPhase('stabilising')
  }

  return { phase, progress, result, start, stop, clear, handleFaceData }
}
